import { z } from 'zod';

// --- Shared base item with common fields ---
const BaseItemSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});

// --- Generic simple name list (name + id) ---
const SimpleNameItemSchema = BaseItemSchema.extend({
  name: z.string(),
});

// --- Disease ---
const DiseaseItemSchema = BaseItemSchema.extend({
  classification: z.string(),
  name: z.string(),
  company_id: z.number().optional(),
  created_by: z.number(),
  updated_by: z.number(),
});

export const DiseaseListSchema = z.array(DiseaseItemSchema);

export const parseDiseaseList = (responseBody) => {
  const result = DiseaseListSchema.safeParse(responseBody?.data);
  if (!result.success) {
    console.warn('[Schema] DiseaseList mismatch:', result.error.flatten());
    return responseBody;
  }
  return { ...responseBody, data: result.data };
};

// --- Currency ---
const CurrencyItemSchema = BaseItemSchema.extend({
  name: z.string(),
  iso_code: z.string(),
  numeric_code: z.string().optional(),
  unicode: z.string().optional(),
  minor_unit: z.number().optional(),
});

export const CurrencyListSchema = z.array(CurrencyItemSchema);

export const parseCurrencyList = (responseBody) => {
  const result = CurrencyListSchema.safeParse(responseBody?.data);
  if (!result.success) {
    console.warn('[Schema] CurrencyList mismatch:', result.error.flatten());
    return responseBody;
  }
  return { ...responseBody, data: result.data };
};

// --- Display Column ---
const DisplayColumnItemSchema = BaseItemSchema.extend({
  column_name: z.string(),
  column_alias: z.string(),
  menu: z.string(),
  display_order: z.number(),
  is_visible: z.boolean(),
  data_type: z.string(),
  translation_code: z.string().optional(),
});

export const DisplayColumnListSchema = z.array(DisplayColumnItemSchema);

export const parseDisplayColumnList = (responseBody) => {
  const result = DisplayColumnListSchema.safeParse(responseBody?.data);
  if (!result.success) {
    console.warn(
      '[Schema] DisplayColumnList mismatch:',
      result.error.flatten()
    );
    return responseBody;
  }
  return { ...responseBody, data: result.data };
};

// --- Doctor ---
const DoctorItemSchema = BaseItemSchema.extend({
  code: z.string(),
  name: z.string(),
  company_id: z.number().nullable().optional(),
  created_by: z.number(),
  updated_by: z.number(),
});

export const DoctorListSchema = z.array(DoctorItemSchema);

export const parseDoctorList = (responseBody) => {
  const result = DoctorListSchema.safeParse(responseBody?.data);
  if (!result.success) {
    console.warn('[Schema] DoctorList mismatch:', result.error.flatten());
    return responseBody;
  }
  return { ...responseBody, data: result.data };
};

// --- Category ---
const CategoryItemSchema = BaseItemSchema.extend({
  company_id: z.number().nullable().optional(),
  category: z.string(),
  label: z.string(),
  display_order: z.number().optional(),
  created_by: z.number(),
  updated_by: z.number(),
});

export const CategoryListSchema = z.array(CategoryItemSchema);

export const parseCategoryList = (responseBody) => {
  const result = CategoryListSchema.safeParse(responseBody?.data);
  if (!result.success) {
    console.warn('[Schema] CategoryList mismatch:', result.error.flatten());
    return responseBody;
  }
  return { ...responseBody, data: result.data };
};

// --- Insurance Category ---
const InsuranceCategoryItemSchema = BaseItemSchema.extend({
  name: z.string(),
  description: z.string().optional(),
  icon: z.string().optional(),
  display_order: z.number().optional(),
  is_multi_claim: z.boolean().optional(),
  created_by: z.number(),
  updated_by: z.number(),
});

export const InsuranceCategoryListSchema = z.array(InsuranceCategoryItemSchema);

export const parseInsuranceCategoryList = (responseBody) => {
  const result = InsuranceCategoryListSchema.safeParse(responseBody?.data);
  if (!result.success) {
    console.warn(
      '[Schema] InsuranceCategoryList mismatch:',
      result.error.flatten()
    );
    return responseBody;
  }
  return { ...responseBody, data: result.data };
};

// --- Generic simple-name list (for endpoints we don't have API docs for) ---
// Used by: getClaimTypeList, getInsuranceList, getInsuranceTypeList,
//          getInsurancePayerList, getMenuList, getNurseList,
//          getOrganizationList, getProcedureList, getRoomList,
//          getServiceClassList, getCityList, getProvinceList, getCountryList
export const SimpleNameListSchema = z.array(SimpleNameItemSchema.passthrough());

export const parseSimpleNameList = (label) => (responseBody) => {
  const result = SimpleNameListSchema.safeParse(responseBody?.data);
  if (!result.success) {
    console.warn(`[Schema] ${label} mismatch:`, result.error.flatten());
    return responseBody;
  }
  return { ...responseBody, data: result.data };
};

// Named parsers for each endpoint using simple list
export const parseClaimTypeList = parseSimpleNameList('ClaimTypeList');
export const parseInsuranceList = parseSimpleNameList('InsuranceList');
export const parseInsuranceTypeList = parseSimpleNameList('InsuranceTypeList');
export const parseInsurancePayerList =
  parseSimpleNameList('InsurancePayerList');
export const parseMenuList = parseSimpleNameList('MenuList');
export const parseNurseList = parseSimpleNameList('NurseList');
export const parseOrganizationList = parseSimpleNameList('OrganizationList');
export const parseProcedureList = parseSimpleNameList('ProcedureList');
export const parseRoomList = parseSimpleNameList('RoomList');
export const parseServiceClassList = parseSimpleNameList('ServiceClassList');
export const parseCityList = parseSimpleNameList('CityList');
export const parseProvinceList = parseSimpleNameList('ProvinceList');
export const parseCountryList = parseSimpleNameList('CountryList');

// --- Options lists (compact shape: id + name only) ---
const OptionItemSchema = z
  .object({ id: z.number(), name: z.string() })
  .passthrough();
export const OptionListSchema = z.array(OptionItemSchema);

export const parseOptionList = (label) => (responseBody) => {
  const result = OptionListSchema.safeParse(responseBody?.data);
  if (!result.success) {
    console.warn(`[Schema] ${label} mismatch:`, result.error.flatten());
    return responseBody;
  }
  return { ...responseBody, data: result.data };
};

export const parseRoleOptions = parseOptionList('RoleOptions');
export const parseUserOptions = parseOptionList('UserOptions');
export const parseCompanyUserOptions = parseOptionList('CompanyUserOptions');
export const parsePatientOptions = parseOptionList('PatientOptions');

// --- TPA Option (company-tpa-identifiers/options) ---
const TpaOptionItemSchema = z.object({
  tpa_id: z.number(),
  tpa_name: z.string(),
});

export const TpaOptionListSchema = z.array(TpaOptionItemSchema);

export const parseTpaOptionList = (responseBody) => {
  const result = TpaOptionListSchema.safeParse(responseBody?.data);
  if (!result.success) {
    console.warn('[Schema] TpaOptionList mismatch:', result.error.flatten());
    return responseBody;
  }
  return { ...responseBody, data: result.data };
};
